import { useEffect, useMemo, useRef, useState } from 'react';
import { summariseCourseProgress } from '@/utils/progressStore';

/**
 * Reactive view over the consolidated progress store. Re-reads on:
 *   - the set of <c>sessionIds</c> changing (different course),
 *   - the <c>userId</c> changing (sign-in / sign-out triggers migration),
 *   - a <c>storage</c> event (other tab updated progress),
 *   - a custom <c>ccms:progress-changed</c> event (same tab, after the player
 *     writes a checkpoint or marks the session complete).
 *
 * Callers commonly hand us a fresh array reference each render (react-query
 * data going through `filter`/`map` in a memo upstream). Depending on the
 * caller's render path that reference can change on every render without
 * the actual IDs changing. To avoid an infinite re-render loop we collapse
 * the IDs to a stable string key and key the effect off that key, not the
 * array reference.
 */
export function useCourseProgress(sessionIds: number[], userId?: string | null) {
  // Deterministic key derived from the IDs themselves. Two arrays with the
  // same numbers in the same order produce the same key, so an upstream
  // reference churn no longer causes us to re-run the effect.
  const idsKey = useMemo(() => sessionIds.join(','), [sessionIds]);

  // Keep the latest IDs in a ref so the event listeners can read them
  // without needing to be re-bound when the array reference changes.
  const idsRef = useRef(sessionIds);
  idsRef.current = sessionIds;

  const [snapshot, setSnapshot] = useState(() => summariseCourseProgress(userId, sessionIds));

  useEffect(() => {
    setSnapshot(summariseCourseProgress(userId, idsRef.current));
    const onChange = () => setSnapshot(summariseCourseProgress(userId, idsRef.current));
    window.addEventListener('storage', onChange);
    window.addEventListener('ccms:progress-changed', onChange as EventListener);
    return () => {
      window.removeEventListener('storage', onChange);
      window.removeEventListener('ccms:progress-changed', onChange as EventListener);
    };
  }, [idsKey, userId]);

  return snapshot;
}
